'use client';

import { useEffect, useRef, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { ChevronDown } from '@gds/Icons/Paths/ChevronDown';
import type { PriceItemAtom } from '@search/Atoms/PriceItemAtom.entity';
import type { PriceTypeAtom } from '@search-filters/Atom/PriceTypeAtom';

import type { searchWidgetMessages } from '../../SearchWidget.messages';
import { getPriceOptionsWithAggs } from '../../Utils/GetPriceOptionsWithAggs';
import { PRICE_OPTIONS } from '../../Utils/PriceUtils';
import { updateFilters } from '../../Utils/UpdateFilters';
import CustomDropDown from '../CustomDropDown/CustomDropDown';
import type { CustomDropDownProps } from '../CustomDropDown/CustomDropDown.entity';

import styles from '../../Search.module.css';

type MaxPriceProps = {
  priceFilterType: PriceTypeAtom;
  strings: Record<keyof typeof searchWidgetMessages, string>;
};

export const MaxPrice = ({ priceFilterType, strings }: MaxPriceProps) => {
  const DEFAULT_OPTION = {
    key: 'any_price',
    displayName: strings.defaultPriceLabel,
    count: 0,
  };
  const filters = useAtomValue(filtersListAtom);
  const aggregations = useAtomValue(aggregationsAtom);
  const setFilters = useSetAtom(filtersListAtom);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const placeholder =
    priceFilterType === 'monthly-price' ? strings.maxMonthlyPriceLabel : strings.maxTotalPriceLabel;
  const filterId = priceFilterType === 'monthly-price' ? 'monthly-price__lte' : 'price__lte';
  const maxPriceFilter = filters.find(({ id }) => id === filterId);
  const maxPrice =
    typeof maxPriceFilter?.value === 'string' ? maxPriceFilter.value : DEFAULT_OPTION.key;

  const correctPriceOptions: PriceItemAtom[] = getPriceOptionsWithAggs(
    PRICE_OPTIONS[priceFilterType],
    aggregations[priceFilterType],
  );
  const allItems = [{ ...DEFAULT_OPTION, count: aggregations.totalCount }, ...correctPriceOptions];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect: NonNullable<CustomDropDownProps['onSelect']> = option => {
    const value = (option as PriceItemAtom).key;
    const filterValue = value === DEFAULT_OPTION.key ? undefined : value;
    setFilters(prev => updateFilters(prev, [{ id: filterId, value: filterValue }]));
    setIsOpen(false);
  };

  const selected = allItems.find(i => i.key === maxPrice);

  return (
    <div
      className={`${styles.makeModelWrapper} ${isOpen ? styles.active : ''}`}
      ref={ref}
      data-test-id="max-price-dropdown"
    >
      <button
        type="button"
        className={styles.dropdownWrapper}
        onClick={() => setIsOpen(o => !o)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={styles.makeValue}>{selected?.displayName ?? placeholder}</span>
        <div className={styles.iconWrapper}>
          <ChevronDown className={isOpen ? styles.rotate : ''} />
        </div>
      </button>
      {isOpen && (
        <CustomDropDown
          items={allItems}
          onSelect={handleSelect}
          checkedValue={maxPrice}
          dataTestId="max-price-menu"
          title={placeholder}
          handleMenuOpen={() => setIsOpen(false)}
          dropdownType="price"
          presorted
        />
      )}
    </div>
  );
};
